import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { VISIBILITY } from '../utils/requestConstants';
import { visibilityLabel } from '../utils/requestHelpers';
import '../../../assets/styles/RequestForm/tiptap.css'

const ToolbarButton = ({ onClick, active, children }) => (
    <Button
        type="button"
        size="sm"
        variant={active ? 'dark' : 'outline-secondary'}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
    >
        {children}
    </Button>
);
const ComposerRichText = ({
    shops,
    onSend,
    isAdmin,
    initialHtml,
    customerName,
    onGenerateMotoOffer,
    quoteLoading,
}) => {
    const [visibility, setVisibility] = useState(
        isAdmin ? VISIBILITY.CUSTOMER_ONLY : VISIBILITY.SUPPLIER_ONLY
    );
    const [notifyCustomer, setNotifyCustomer] = useState(false);
    const [notifyShops, setNotifyShops] = useState(false);
    const [notifyShopIds, setNotifyShopIds] = useState(new Set());
    const [sending, setSending] = useState(false);
    const [editorText, setEditorText] = useState('');
    const hasSelection = () => {
        if (!editor) return false;
        const { from, to } = editor.state.selection;
        return from !== to;
    };

    const removeStoredMark = (markName) => {
        if (!editor) return;

        const markType = editor.state.schema.marks[markName];
        if (!markType) return;

        const tr = editor.state.tr.removeStoredMark(markType);
        editor.view.dispatch(tr);
    };

    const resetTypingMarks = () => {
        removeStoredMark('bold');
        removeStoredMark('link');
    };

    const editor = useEditor({
        extensions: [
            StarterKit,
            Link.configure({
                openOnClick: true,
                autolink: true,
                linkOnPaste: true,
            }),
            Placeholder.configure({
                placeholder: isAdmin ? 'Write your reply…' : 'Write your message…',
            }),
        ],
        content: initialHtml || '<p></p>',
        immediatelyRender: false,
        onUpdate: ({ editor }) => {
            setEditorText(editor.getText().trim());
        },
    });

    const handleBold = () => {
        if (!editor) return;

        if (!hasSelection()) {
            alert('Please select the text first, then apply bold.');
            return;
        }

        editor.chain().focus().toggleBold().run();
        resetTypingMarks();
    };

    const handleLink = () => {
        if (!editor) return;

        if (!hasSelection()) {
            alert('Please select the text first, then add a link.');
            return;
        }

        const previousUrl = editor.getAttributes('link').href || '';
        const url = window.prompt('Enter URL', previousUrl);

        if (url === null) return;

        if (!url.trim()) {
            editor.chain().focus().unsetLink().run();
            removeStoredMark('link');
            return;
        }

        editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
        removeStoredMark('link');
    };

    const handleBulletList = () => {
        if (!editor) return;
        editor.chain().focus().toggleBulletList().run();
    };

    const handleOrderedList = () => {
        if (!editor) return;
        editor.chain().focus().toggleOrderedList().run();
    };

    const handleClearFormatting = () => {
        if (!editor) return;

        editor.chain().focus().unsetAllMarks().clearNodes().run();
        resetTypingMarks();
    };

    useEffect(() => {
        if (!editor) return;
        const content = initialHtml || '<p></p>';
        editor.commands.setContent(content, false);
        setEditorText(editor.getText().trim());
    }, [editor, initialHtml]);

    useEffect(() => {
        if (!isAdmin) {
            setVisibility(VISIBILITY.SUPPLIER_ONLY);
            setNotifyCustomer(false);
            setNotifyShops(false);
            setNotifyShopIds(new Set());
            return;
        }

        if (visibility === VISIBILITY.INTERNAL) {
            setNotifyCustomer(false);
            setNotifyShops(false);
            setNotifyShopIds(new Set());
            return;
        }

        if (visibility === VISIBILITY.CUSTOMER_ONLY) {
            setNotifyCustomer(true);
            setNotifyShops(false);
            setNotifyShopIds(new Set());
            return;
        }

        if (visibility === VISIBILITY.SUPPLIER_ONLY) {
            setNotifyCustomer(false);
            setNotifyShops(true);
            setNotifyShopIds(new Set((shops || []).map((s) => s.shopId).filter(Boolean)));
            return;
        }

        if (visibility === VISIBILITY.PUBLIC_ALL) {
            setNotifyCustomer(true);
            setNotifyShops((shops || []).length > 0);
            setNotifyShopIds(new Set((shops || []).map((s) => s.shopId).filter(Boolean)));
        }
    }, [visibility, shops, isAdmin]);

    const toggleShopNotify = (shopId) => {
        setNotifyShopIds((prev) => {
            const next = new Set(prev);
            if (next.has(shopId)) next.delete(shopId);
            else next.add(shopId);
            return next;
        });
    };

    const handleSend = async () => {
        if (!editor) return;

        /* const html = editor.getHTML(); */
        const json = JSON.stringify(editor.getJSON());
        const text = editor.getText().trim();

        if (!text) return;

        if (isAdmin && visibility === VISIBILITY.SUPPLIER_ONLY && notifyShops && notifyShopIds.size === 0) {
            alert('Please select at least one shop to notify.');
            return;
        }

        try {
            setSending(true);

            const payloadBase = {
                content: json,
            };

            if (!isAdmin) {
                await onSend({
                    ...payloadBase,
                    visibility: VISIBILITY.SUPPLIER_ONLY,
                    notifyCustomer: false,
                    notifyShopIds: [],
                });
            } else {
                await onSend({
                    ...payloadBase,
                    visibility,
                    notifyCustomer: !!notifyCustomer,
                    notifyShopIds: notifyShops ? Array.from(notifyShopIds) : [],
                });
            }

            editor.commands.clearContent();
            setEditorText('');
        } finally {
            setSending(false);
        }
    };

    const setLink = () => {
        if (!editor) return;
        const previousUrl = editor.getAttributes('link').href || '';
        const url = window.prompt('Enter URL', previousUrl);

        if (url === null) return;

        if (url === '') {
            editor.chain().focus().unsetLink().run();
            return;
        }

        editor.chain().focus().setLink({ href: url }).run();
    };

    const isInternal = isAdmin && visibility === VISIBILITY.INTERNAL;
    const showShopNotifyList = isAdmin && notifyShops && !isInternal && (shops || []).length > 0;

    const safeCustomerName = String(customerName || 'Customer').trim() || 'Customer';

    const messageTemplates = {
        template1: `<p>Hello ${safeCustomerName},</p>
<p>Thank you very much for contacting us. Could you please provide more information about the model of the motorcycle and the dates you are interested in renting it? It would be a pleasure to assist you.</p>
<p>Best Regards,</p>
<p>RMB</p>`,

        template2: `<p>Hello ${safeCustomerName},</p>
<p>Thank you very much for using our service. If you encounter any issues with your booking or payment, please don't hesitate to let us know. It would be a pleasure to assist you.</p>
<p>Best Regards,</p>
<p>RMB</p>`,

        template3: `<p>Dear ${safeCustomerName},</p>
<p>Did you receive the offer? If you have some doubts, please let us know, and we will help you. From our side, we would like to offer you a 5% discount with code <strong>RMDTO5</strong> for your next booking.</p>
<p>Best Regards,</p>
<p>RMB</p>`,
    };

    const applyTemplate = (key) => {
        if (!editor) return;
        editor.commands.setContent(messageTemplates[key] || '<p></p>', true);
    };

    return (
        <Card id="request-composer" className="p-3 mt-3">
            <Form.Group>
                <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                    <Form.Label className="fw-semibold mb-0">
                        {isAdmin ? 'Reply' : 'Message'}
                    </Form.Label>

                    {isAdmin && (
                        <div className="d-flex flex-wrap gap-2">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={onGenerateMotoOffer}
                                disabled={quoteLoading}
                            >
                                {quoteLoading ? 'Generating…' : 'Generate moto offer'}
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => applyTemplate('template1')}
                            >
                                Ask for model & dates
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => applyTemplate('template2')}
                            >
                                Help with booking/payment
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => applyTemplate('template3')}
                            >
                                Follow up offer + discount
                            </Button>
                        </div>
                    )}
                </div>

                <div className="d-flex flex-wrap gap-2 mb-2">
                    <ToolbarButton
                        active={editor?.isActive('bold')}
                        onClick={handleBold}
                        title="Bold"
                    >
                        <strong>B</strong>
                    </ToolbarButton>

                    <ToolbarButton
                        active={false}
                        onClick={handleClearFormatting}
                    >
                        Normal
                    </ToolbarButton>

                    <ToolbarButton
                        active={editor?.isActive('bulletList')}
                        onClick={handleBulletList}
                        title="Bullet list"
                    >
                        • List
                    </ToolbarButton>

                    <ToolbarButton
                        active={editor?.isActive('orderedList')}
                        onClick={handleOrderedList}
                        title="Numbered list"
                    >
                        1. List
                    </ToolbarButton>

                    <ToolbarButton
                        active={editor?.isActive('link')}
                        onClick={handleLink}
                        title="Insert link"
                    >
                        🔗
                    </ToolbarButton>

                </div>
                <div
                    className="rich-editor p-2 bg-white"
                    style={{
                        minHeight: 220,
                        maxHeight: 320,
                        overflowY: 'auto',
                    }}
                >
                    <EditorContent editor={editor} className="tiptap" />
                </div>

                {isInternal && (
                    <div className="text-warning-emphasis small mt-1">
                        Internal only. No customer/shop visibility or email notification.
                    </div>
                )}
            </Form.Group>

            {isAdmin && (
                <div className="mt-2 pt-2 border-top">
                    <Row className="g-2 align-items-start">
                        <Col md={5}>
                            <Form.Label className="small fw-semibold mb-1">Visible to</Form.Label>
                            <Form.Select
                                size="sm"
                                value={visibility}
                                onChange={(e) => setVisibility(e.target.value)}
                            >
                                <option value={VISIBILITY.PUBLIC_ALL}>{visibilityLabel(VISIBILITY.PUBLIC_ALL)}</option>
                                <option value={VISIBILITY.CUSTOMER_ONLY}>{visibilityLabel(VISIBILITY.CUSTOMER_ONLY)}</option>
                                <option value={VISIBILITY.SUPPLIER_ONLY}>{visibilityLabel(VISIBILITY.SUPPLIER_ONLY)}</option>
                                <option value={VISIBILITY.INTERNAL}>{visibilityLabel(VISIBILITY.INTERNAL)}</option>
                            </Form.Select>
                        </Col>

                        <Col md={7}>
                            <Form.Label className="small fw-semibold mb-1">Email notification</Form.Label>
                            <div className="d-flex flex-column gap-1 small">
                                <Form.Check
                                    type="checkbox"
                                    id="notifyCustomer"
                                    label="Customer"
                                    checked={!!notifyCustomer}
                                    disabled={isInternal || visibility === VISIBILITY.SUPPLIER_ONLY}
                                    onChange={(e) => setNotifyCustomer(e.target.checked)}
                                />

                                <Form.Check
                                    type="checkbox"
                                    id="notifyShops"
                                    label="Shops"
                                    checked={!!notifyShops}
                                    disabled={isInternal || visibility === VISIBILITY.CUSTOMER_ONLY || (shops || []).length === 0}
                                    onChange={(e) => setNotifyShops(e.target.checked)}
                                />
                            </div>
                        </Col>
                    </Row>

                    {showShopNotifyList && (
                        <div className="mt-2 border rounded p-2 bg-white">
                            <div className="text-muted small mb-1">Notify these shops</div>
                            <div className="d-flex flex-column gap-1" style={{ maxHeight: 100, overflowY: 'auto' }}>
                                {(shops || []).map((s) => (
                                    <Form.Check
                                        key={s.shopId}
                                        type="checkbox"
                                        id={`notifyShop_${s.shopId}`}
                                        checked={notifyShopIds.has(s.shopId)}
                                        onChange={() => toggleShopNotify(s.shopId)}
                                        label={s.name}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="d-flex justify-content-end mt-2">
                <Button
                    type="button"
                    className="border ms-1 btn-custom-success"
                    size="sm"
                    disabled={sending || !editorText}
                    onClick={handleSend}
                >
                    {sending ? 'Sending…' : 'Send'}
                </Button>
            </div>
        </Card>
    );
};

export default ComposerRichText;